import React, { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Link } from "react-router-dom";
import { Toaster } from "sonner";
import { TenantProvider } from "./context/TenantContext";
import { CurrencyProvider } from "./context/CurrencyContext";
import { AuthProvider } from "./context/AuthContext";
import { authSession, IS_SAAS_HOST } from "./lib/api";
import { trackPageView } from "./lib/analytics";
import Designer from "./pages/Designer";
import OrderReview from "./pages/OrderReview";
import Payment from "./pages/Payment";
import OrderStatus from "./pages/OrderStatus";
import Admin from "./pages/Admin";
import Login from "./pages/saas/Login";
import Signup from "./pages/saas/Signup";
import Onboarding from "./pages/saas/Onboarding";
import AcceptInvite from "./pages/saas/AcceptInvite";
import { ForgotPassword, ResetPassword } from "./pages/saas/ForgotReset";
import Account from "./pages/saas/Account";
import PlatformConsole from "./pages/saas/PlatformConsole";
import PlatformBilling from "./pages/saas/PlatformBilling";
import PlatformAnalytics from "./pages/saas/PlatformAnalytics";
import TenantDashboard from "./pages/saas/TenantDashboard";
import BillingReturn from "./pages/saas/BillingReturn";
import MarketingHome from "./pages/marketing/Home";
import Pricing from "./pages/marketing/Pricing";
import Contact from "./pages/marketing/Contact";
import Demo from "./pages/marketing/Demo";
import SeoLanding from "./pages/marketing/Seo";
import PrivacyPolicy from "./pages/marketing/PrivacyPolicy";
import Terms from "./pages/marketing/Terms";
import ShopifyAdmin from "./pages/shopify/ShopifyAdmin";
import ShopifyBillingResolution from "./pages/shopify/ShopifyBillingResolution";

function AuthCallback() {
  const navigate = useNavigate();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const sid = params.get("session_id");
    if (!sid) { navigate("/admin", { replace: true }); return; }
    authSession(sid).then(() => {
      window.history.replaceState(null, "", "/admin");
      navigate("/admin", { replace: true });
    }).catch(() => navigate("/admin", { replace: true }));
  }, [navigate]);
  return (
    <div className="min-h-screen flex items-center justify-center bg-pgt-bg">
      <div className="text-pgt-green font-display text-2xl tracking-widest pgt-pulse">SIGNING IN…</div>
    </div>
  );
}

function NotFound() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#0a0a0a] text-white px-6 text-center" data-testid="not-found">
      <div className="text-[#00D26A] text-xs tracking-[0.3em] uppercase mb-3">404 — Page not found</div>
      <h1 className="text-3xl md:text-5xl font-black uppercase tracking-tighter" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>This page doesn't exist</h1>
      <p className="text-white/50 mt-4 max-w-md">The link may be broken or the page may have moved.</p>
      <div className="mt-8 flex flex-wrap gap-3 justify-center">
        <Link to="/software" className="bg-[#00D26A] text-black font-bold uppercase tracking-widest text-sm px-6 py-3 rounded-md hover:bg-[#00E574] transition-colors" data-testid="notfound-software">Go to AI Turf Designer</Link>
        <a href="/" className="border border-white/20 text-white font-bold uppercase tracking-widest text-sm px-6 py-3 rounded-md hover:bg-white/5 transition-colors" data-testid="notfound-home">Prime Gym Turf</a>
      </div>
    </div>
  );
}

function AnalyticsListener() {
  const location = useLocation();
  useEffect(() => { trackPageView(location.pathname); }, [location.pathname]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  // Shopify embedded bootstrap: when Shopify loads the app (its embed params `host`+`shop` are present)
  // at any non-Shopify path, route straight to the embedded admin. App Bridge then authenticates the
  // merchant via a session token — no standalone /login. Non-Shopify SaaS users are never affected.
  const _sp = new URLSearchParams(location.search);
  if (_sp.get("host") && _sp.get("shop") && !location.pathname.startsWith("/shopify") && location.pathname !== "/api/shopify/app") {
    window.location.replace(`/shopify/admin${location.search}`);
    return null;
  }
  if (location.hash && location.hash.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      {/* Shopify App URL: the backend serves this same SPA document with the shop-specific CSP header */}
      <Route path="/api/shopify/app" element={<ShopifyAdmin />} />
      <Route path="/" element={IS_SAAS_HOST ? <MarketingHome /> : <Designer />} />
      <Route path="/embed/:slug" element={<Designer embed />} />
      <Route path="/t/:slug" element={<Designer embed />} />
      <Route path="/design" element={<Designer />} />
      <Route path="/design/:designId" element={<Designer />} />
      <Route path="/review" element={<OrderReview />} />
      <Route path="/payment/success" element={<Payment />} />
      <Route path="/payment/cancel" element={<Payment />} />
      <Route path="/order/:orderNumber" element={<OrderStatus />} />
      <Route path="/admin" element={<Admin />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/invite/:token" element={<AcceptInvite />} />
      <Route path="/forgot" element={<ForgotPassword />} />
      <Route path="/reset/:token" element={<ResetPassword />} />
      <Route path="/account" element={<Account />} />
      <Route path="/platform" element={<PlatformConsole />} />
      <Route path="/platform/billing" element={<PlatformBilling />} />
      <Route path="/platform/analytics" element={<PlatformAnalytics />} />
      <Route path="/dashboard" element={<TenantDashboard />} />
      <Route path="/billing/return" element={<BillingReturn />} />
      <Route path="/software" element={<MarketingHome />} />
      <Route path="/saas" element={<MarketingHome />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/demo" element={<Demo />} />
      <Route path="/gym-turf-designer" element={<SeoLanding slug="gym-turf-designer" />} />
      <Route path="/artificial-turf-designer" element={<SeoLanding slug="artificial-turf-designer" />} />
      <Route path="/gym-flooring-software" element={<SeoLanding slug="gym-flooring-software" />} />
      <Route path="/turf-visualiser" element={<SeoLanding slug="turf-visualiser" />} />
      <Route path="/shopify/admin" element={<ShopifyAdmin />} />
      <Route path="/shopify-review/billing-resolution" element={<ShopifyBillingResolution />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <TenantProvider>
        <CurrencyProvider>
          <BrowserRouter>
            <Toaster theme="dark" position="top-center" richColors />
            <AnalyticsListener />
            <AppRouter />
          </BrowserRouter>
        </CurrencyProvider>
      </TenantProvider>
    </AuthProvider>
  );
}
